import React, { useState, useEffect } from "react";
import {
  Box,
  Collapse,
  IconButton,
  Typography,
  Skeleton,
  Pagination,
  Button,
  Badge
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";

const CollapsibleDataGridTable = ({
  parentRows = [],
  parentColumns = [],
  childColumns = [],
  childFieldName = "children",
  initialPageSize = 5,
  initialRowHeight = 52,
  getChildColumnsForRow = null, // Optional function to get columns per row
}) => {
  const [openRows, setOpenRows] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const pageSize = initialPageSize || 5;
  const totalPages = Math.ceil(parentRows.length / pageSize);

  const paginatedRows = parentRows.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  // const toggleRow = (id) => {
  //   setOpenRows((prev) => ({ ...prev, [id]: !prev[id] }));
  // };

  const toggleRow = (id) => {
  setOpenRows((prev) => {
    // If the clicked row is already open, close it
    if (prev[id]) return {};
    // Otherwise, close all others and open only this one
    return { [id]: true };
  });
};

  // Expand/collapse button column
  const expandableColumn = {
    field: "expand",
    headerName: "",
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    width: 60,
    renderCell: (params) => {
      const childRowCount = params.row[childFieldName]?.length || 0;

      return (
        <Badge
          badgeContent={childRowCount}
          color="primary"
          overlap="circular"
          showZero
        >
          <IconButton
            size="small"
            onClick={() => toggleRow(params.row.id)}
            sx={{ transition: "0.3s" }}
          >
            {openRows[params.row.id] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </Badge>
      );
    },
  };

  const columns = [expandableColumn, ...parentColumns];
  const rowsWithId = paginatedRows.map((row, i) => ({ ...row, id: i }));

  useEffect(() => {
    setTimeout(() => setLoading(false), 100);
  }, []);

  const renderSkeletonRows = () => (
    Array(5)
      .fill(0)
      .map((_, i) => (
        <Skeleton key={i} variant="rectangular" height={40} sx={{ my: 1 }} />
      ))
  );

  return (
    <Box sx={{ mt: 2 }}>
      {loading ? (
        <Box
          sx={{
            borderRadius: 2,
            boxShadow: 1,
            p: 2,
          }}
        >
          {renderSkeletonRows()}
        </Box>
      ) : (
        <Box
          sx={{
            borderRadius: 1,
            boxShadow: 1,
            border: "1px solid #dde7eb80",
            backgroundColor: "#fff",
          }}
        >
          {/* ✅ Single header rendered using DataGrid (empty rows) */}
          {/* <DataGrid
            autoHeight
            rows={[]} // header only
            columns={columns}
            hideFooter
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#ffdddb !important",
              },
              "& .MuiDataGrid-columnHeaderTitleContainer": {
                justifyContent: "center",
              },
              "& .MuiDataGrid-columnHeader": {
                fontWeight: "bold",
                textAlign: "center",
              },
               "& .MuiDataGrid-overlay": {
              display: "none !important",   // 👈 hides "No rows"
            },
            }}
          /> */}
          <Box sx={{ display: "flex", backgroundColor: "#ffdddb", fontWeight: "bold" }}>
          {columns.map((col, i) => (
            <Box
              key={i}
              sx={{
                flex: col.flex || "1",
                minWidth: col.width || 120,
                textAlign: "center",
                py: 1.5,
                fontSize: "0.875rem", // 👈 matches the default DataGridTable font size
              }}
            >
              {col.headerName}
            </Box>
          ))}
        </Box>

          {/* ✅ Parent rows with collapsible children rendered manually */}
          {rowsWithId.map((row) => (
            <Box key={row.id}>
              {/* Parent Row */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderBottom: "1px solid #e0e0e0",
                  backgroundColor:
                    row.id % 2 === 0 ? "#ffffff" : "#f9f9f9",
                  "&:hover": { backgroundColor: "#f5f5f5" },
                  fontSize: "0.875rem", // 👈 matches the default DataGridTable font size
                }}
              >
                {columns.map((col, colIndex) => {
                  const value =
                    col.field === "expand"
                      ? col.renderCell({ row })
                      : col.renderCell
                      ? col.renderCell({ row, value: row[col.field] })
                      : row[col.field];

                  return (
                    <Box
                      key={colIndex}
                      sx={{
                        flex: col.flex || "1",
                        minWidth: col.width || 120,
                        textAlign: "center",
                        py: 1.5,
                      }}
                    >
                      {value}
                    </Box>
                  );
                })}
              </Box>

              {/* ✅ Child Collapse right below its parent */}
              <Collapse in={openRows[row.id]} timeout="auto" unmountOnExit>
                <Box
                  sx={{
                    backgroundColor: "#fafafa",
                    p: 2,
                    borderTop: "1px solid #e0e0e0",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, color: "#444", fontWeight: 600 , display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center", //vertically center 
                  }}
                  >
                    {/* Details for {row.name || row.title || `Row ${row.id + 1}`} */} DLT Account Details
                    {/* <Button
                      onClick={() =>
                      handleTransactionHistory(
                        row.children[0].entityId == null ? row.children[0].userId : row.children[0].entityId,
                        row.children[0].listingId,
                        row?.propertyName,
                        row.rightToOwnerShip
                      )
                      }
                      style={{ padding: "12px 15px" }}
                      variant="outlined"
                      color="primary"
                    >
                      Transaction History
                    </Button> */}
                  </Typography>
                  <DataGrid
                    autoHeight
                    rows={
                      row[childFieldName]?.map((child, i) => ({
                        ...child,
                        id: `${row.id}-${i}`,
                      })) || []
                    }
                    columns={getChildColumnsForRow ? getChildColumnsForRow(row) : childColumns}
                    disableSelectionOnClick
                    disableColumnMenu
                    hideFooter
                    rowHeight={initialRowHeight}
                    sx={{
                      border: "1px solid #e0e0e0",
                      width: "100%",
                      "& .MuiDataGrid-cell:focus": { outline: "none" },
                      "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: "#f5f5f5",
                      },
                      "& .MuiDataGrid-columnHeaderTitleContainer": {
                        justifyContent: "center",
                      },
                      "& .MuiDataGrid-cell--textLeft": {
                        textAlign: "center",
                      },
                    }}
                  />
                </Box>
              </Collapse>
            </Box>
          ))}

          {/* ✅ Pagination (bottom) */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 2,
              borderTop: "1px solid #dde7eb80",
              backgroundColor: "#fff",
            }}
          >
            <Pagination
              count={Math.max(totalPages, 1)}
              page={page}
              onChange={(e, newPage) => setPage(newPage)}
              color="primary"
              shape="rounded"
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CollapsibleDataGridTable;
